import React, { useState } from "react";
import { Image, Maximize2, Paperclip, Trash2, Upload } from "react-feather";
import toast from "react-hot-toast";
import SectionCard from "../../../../components/common/SectionCard";
import AttachmentPreviewModal from "../../../../components/common/AttachmentPreviewModal";
import InlineFilePreview from "../../../pettycash/components/InlineFilePreview";
import {
  useDeleteSurveyAttachmentMutation,
  useGetSurveyAttachmentContentQuery,
  useLazyGetSurveyAttachmentContentQuery,
  useUploadSurveyAttachmentMutation,
} from "../surveysApi";
import { ATTACHMENT_KINDS, formatBytes, formatDate } from "../surveyStatusMaps";

const MAX_ATTACHMENTS = 20;
const MB = 1024 * 1024;
// Mirrors the server's rules so a bad file is refused before the upload.
const RULES = {
  PHOTO: { types: ["image/jpeg", "image/png"], maxBytes: 6 * MB, text: "JPEG or PNG, up to 6 MB" },
  DRAWING: { types: ["image/jpeg", "image/png", "application/pdf"], maxBytes: 5 * MB, text: "JPEG, PNG or PDF, up to 5 MB" },
  DOCUMENT: { types: ["image/jpeg", "image/png", "application/pdf"], maxBytes: 5 * MB, text: "JPEG, PNG or PDF, up to 5 MB" },
};

const Thumb = ({ recordGuid, attachment, onOpen }) => {
  const { data: blob, isError } = useGetSurveyAttachmentContentQuery({ recordGuid, attachmentGuid: attachment.recordGuid });
  return (
    <InlineFilePreview
      blob={blob}
      fileName={attachment.originalFileName}
      onOpen={onOpen}
      width={170}
      height={170}
      loading={isError ? false : undefined}
    />
  );
};

/**
 * The survey's photos, drawings and documents, grouped by kind, each shown inline
 * (picture or PDF first page) and opened full size in the shared preview modal.
 * Add/delete follows the detail's canEdit flag.
 */
const SurveyAttachments = ({ recordGuid, attachments = [], canEdit }) => {
  const [upload, { isLoading: isUploading }] = useUploadSurveyAttachmentMutation();
  const [deleteAttachment] = useDeleteSurveyAttachmentMutation();
  const [fetchContent] = useLazyGetSurveyAttachmentContentQuery();
  const [kind, setKind] = useState("PHOTO");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null); // { blob, attachment }
  const inputId = `survey-attach-${recordGuid}`;

  const pickFiles = (list) => {
    const rule = RULES[kind];
    const ok = [];
    for (const f of list) {
      if (!rule.types.includes(f.type)) toast.error(`${f.name}: ${rule.text}`);
      else if (f.size > rule.maxBytes) toast.error(`${f.name}: too large (${rule.text})`);
      else ok.push(f);
    }
    setFiles(ok);
  };

  const handleUpload = async () => {
    if (files.length === 0) return toast.error("Choose one or more files first");
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      return toast.error(`A survey can hold at most ${MAX_ATTACHMENTS} attachments (${attachments.length} already)`);
    }
    let uploaded = 0;
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await upload({ recordGuid, file, kind, caption: caption.trim() || undefined }).unwrap();
        uploaded += 1;
      } catch (error) {
        toast.error(`${file.name}: ${error?.message || "could not upload"}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} attached`);
    setFiles([]);
    setCaption("");
    const input = document.getElementById(inputId);
    if (input) input.value = "";
  };

  const handleOpen = async (attachment) => {
    try {
      const blob = await fetchContent({ recordGuid, attachmentGuid: attachment.recordGuid }).unwrap();
      setPreview({ blob, attachment });
    } catch (error) {
      toast.error(error?.message || "Could not open the file");
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Remove "${attachment.originalFileName}"?`)) return;
    try {
      await deleteAttachment({ recordGuid, attachmentGuid: attachment.recordGuid }).unwrap();
      toast.success("Attachment removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the attachment");
    }
  };

  return (
    <SectionCard
      icon={Paperclip}
      title="Photos, Drawings & Documents"
      right={<span className="badge badge-linesecondary">{attachments.length}</span>}
    >
      {canEdit && (
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center p-2 rounded" style={{ border: "1px dashed #d8b9be", background: "#fdf7f8" }}>
          <select
            className="form-control form-control-sm"
            style={{ width: 130 }}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setFiles([]);
              const input = document.getElementById(inputId);
              if (input) input.value = "";
            }}
            disabled={isUploading}
          >
            {ATTACHMENT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label.replace(/s$/, "")}</option>
            ))}
          </select>
          <input
            id={inputId}
            type="file"
            multiple
            accept={kind === "PHOTO" ? ".jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.pdf"}
            className="form-control form-control-sm"
            style={{ maxWidth: 300 }}
            onChange={(e) => pickFiles([...(e.target.files || [])])}
            disabled={isUploading}
          />
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 220 }}
            placeholder="Caption (optional)"
            maxLength={200}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={isUploading}
          />
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleUpload} disabled={isUploading || files.length === 0}>
            <Upload size={14} className="me-1" /> {isUploading ? "Uploading…" : `Attach${files.length > 1 ? ` ${files.length} files` : ""}`}
          </button>
          <small className="text-muted">{RULES[kind].text}. Max {MAX_ATTACHMENTS} per survey.</small>
        </div>
      )}

      {attachments.length === 0 && <p className="text-muted small mb-0">Nothing attached.</p>}

      {ATTACHMENT_KINDS.map((k) => {
        const group = attachments.filter((a) => (a.kind || "PHOTO") === k.value);
        if (group.length === 0) return null;
        return (
          <div key={k.value} className="mb-3">
            <div className="fw-semibold small text-uppercase mb-2 d-flex align-items-center" style={{ letterSpacing: 0.4, color: "#6c757d" }}>
              <Image size={13} className="me-1" /> {k.label} <span className="ms-1">({group.length})</span>
            </div>
            <div className="d-flex flex-wrap gap-3">
              {group.map((a) => (
                <div key={a.recordGuid} style={{ width: 170 }}>
                  <Thumb recordGuid={recordGuid} attachment={a} onOpen={() => handleOpen(a)} />
                  <div className="small mt-1 d-flex justify-content-between align-items-start gap-1">
                    <div style={{ minWidth: 0 }}>
                      {a.caption && <div className="fw-semibold text-truncate" title={a.caption}>{a.caption}</div>}
                      <div className="text-truncate text-muted" title={a.originalFileName}>{a.originalFileName}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {a.uploadedByName || "-"} · {formatDate(a.uploadedDate)} · {formatBytes(a.fileSizeBytes)}
                      </div>
                    </div>
                    <div className="d-flex flex-column">
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => handleOpen(a)} title="Open full size">
                        <Maximize2 size={14} />
                      </button>
                      {canEdit && (
                        <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => handleDelete(a)} title="Remove">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <AttachmentPreviewModal
        show={Boolean(preview)}
        onClose={() => setPreview(null)}
        blob={preview?.blob}
        fileName={preview?.attachment.originalFileName}
        title={preview ? preview.attachment.caption || preview.attachment.originalFileName : ""}
      />
    </SectionCard>
  );
};

export default SurveyAttachments;
